import { useState } from "react";

import {
  auth as authApi,
  invitations as invitationsApi,
  members as membersApi,
} from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  StatusBadge,
  Tabs,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";
import type { Invitation } from "../lib/types";

export function TeamPage() {
  const { organization, organizationId } = useCurrentOrganization();
  const { can } = useAuth();
  const [tab, setTab] = useState<"members" | "invitations">("members");
  const [nonce, setNonce] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [justSent, setJustSent] = useState<Invitation | undefined>();
  const [historyFor, setHistoryFor] = useState<Invitation | undefined>();

  const members = useAsync(
    () => (organizationId ? membersApi.list(organizationId) : Promise.resolve([])),
    [organizationId, nonce],
  );
  const invites = useAsync(
    () => (organizationId ? invitationsApi.list(organizationId) : Promise.resolve([])),
    [organizationId, nonce],
  );
  const roles = useAsync(() => authApi.roles(), []);
  const assignable = useAsync(
    () => (organizationId ? membersApi.assignableRoles(organizationId) : Promise.resolve([])),
    [organizationId],
  );

  const mayManage = can(organizationId, "members:write");

  if (!organizationId || !organization) {
    return (
      <>
        <PageHeader title="Team" />
        <Card>
          <EmptyState title="No organization selected" />
        </Card>
      </>
    );
  }

  async function run(action: () => Promise<unknown>) {
    setError(undefined);
    try {
      await action();
      setNonce((n) => n + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const pending = (invites.data ?? []).filter((row) => row.status === "pending");

  return (
    <>
      <PageHeader
        title="Team"
        description="Who can see this organization, what they can do, and who has been invited but not arrived yet."
        actions={
          mayManage ? (
            <>
              <button
                onClick={() =>
                  void run(async () => {
                    const result = await invitationsApi.fromPeople(organizationId);
                    setError(
                      result.failed.length
                        ? `Invited ${result.sent.length}. Skipped: ${result.failed
                            .map((row) => `${row.email} (${row.reason})`)
                            .join("; ")}`
                        : undefined,
                    );
                  })
                }
                title="Invite everybody already recorded as an interview subject who has an email address"
              >
                Invite recorded people
              </button>
              <button className="primary" onClick={() => setInviting(true)}>
                Invite someone
              </button>
            </>
          ) : null
        }
      />

      <div className="stack">
        <ErrorBanner error={error ?? members.error ?? invites.error} />

        {justSent ? (
          <div className="banner banner--ok">
            <div>
              Invitation sent to <strong>{justSent.email}</strong>
              {justSent.delivery_status === "failed"
                ? " — but the email could not be delivered. Share the link directly:"
                : ". They can also use this link directly:"}
            </div>
            <div className="row" style={{ marginTop: 6 }}>
              <input readOnly value={justSent.invite_url ?? ""} style={{ flex: 1 }} />
              <button
                onClick={() => void navigator.clipboard?.writeText(justSent.invite_url ?? "")}
              >
                Copy
              </button>
              <button className="btn--ghost" onClick={() => setJustSent(undefined)}>
                Dismiss
              </button>
            </div>
            <div className="tiny faint" style={{ marginTop: 6 }}>
              This link is shown once. Resending the invitation issues a new one and retires this.
            </div>
          </div>
        ) : null}

        <Tabs
          value={tab}
          onChange={setTab}
          options={[
            { value: "members", label: "Members", count: members.data?.length ?? 0 },
            { value: "invitations", label: "Invitations", count: pending.length },
          ]}
        />

        {tab === "members" ? (
          <Card flush>
            {members.loading ? (
              <Loading />
            ) : !members.data?.length ? (
              <EmptyState
                title="Nobody else has access yet"
                description="Invite the people who actually run the operation — they are the ones who know how it works."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Role</th>
                      <th>Last signed in</th>
                      <th>Invited by</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {members.data.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <strong>{member.full_name ?? member.email}</strong>
                          {member.full_name ? (
                            <div className="tiny faint">{member.email}</div>
                          ) : null}
                        </td>
                        <td>
                          {mayManage ? (
                            <select
                              value={member.role_code}
                              onChange={(event) =>
                                void run(() =>
                                  membersApi.changeRole(
                                    organizationId,
                                    member.id,
                                    event.target.value,
                                  ),
                                )
                              }
                            >
                              {(assignable.data ?? [member.role_code]).map((code) => (
                                <option key={code} value={code}>
                                  {roles.data?.find((role) => role.code === code)?.label ?? code}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="badge badge--neutral">{member.role_label}</span>
                          )}
                        </td>
                        <td className="small muted">
                          {member.last_login_at
                            ? new Date(member.last_login_at).toLocaleDateString()
                            : "never"}
                        </td>
                        <td className="small muted">{member.invited_by ?? "—"}</td>
                        <td>
                          {mayManage ? (
                            <button
                              className="btn--ghost"
                              onClick={() =>
                                void run(() => membersApi.remove(organizationId, member.id))
                              }
                            >
                              Remove
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}

        {tab === "invitations" ? (
          <Card flush>
            {invites.loading ? (
              <Loading />
            ) : !invites.data?.length ? (
              <EmptyState
                title="No invitations yet"
                description="An invitation emails a link that signs the recipient straight into the questions you need answered."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Expires</th>
                      <th>Reminders</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {invites.data.map((invitation) => (
                      <tr key={invitation.id}>
                        <td>
                          {invitation.email}
                          {invitation.full_name ? (
                            <div className="tiny faint">{invitation.full_name}</div>
                          ) : null}
                        </td>
                        <td className="small muted">{invitation.role_code}</td>
                        <td>
                          <StatusBadge status={invitation.status} />
                        </td>
                        <td className="small muted">
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </td>
                        <td className="small muted">{invitation.reminder_count}</td>
                        <td>
                          <div className="row" style={{ gap: 4 }}>
                            <button
                              className="btn--ghost"
                              onClick={() => setHistoryFor(invitation)}
                            >
                              Emails
                            </button>
                            {mayManage && invitation.status === "pending" ? (
                              <>
                                <button
                                  className="btn--ghost"
                                  onClick={() =>
                                    void run(async () => {
                                      const sent = await invitationsApi.resend(
                                        organizationId,
                                        invitation.id,
                                      );
                                      setJustSent(sent);
                                    })
                                  }
                                >
                                  Resend
                                </button>
                                <button
                                  className="btn--ghost"
                                  onClick={() =>
                                    void run(() =>
                                      invitationsApi.revoke(organizationId, invitation.id),
                                    )
                                  }
                                >
                                  Revoke
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}

        <Card title="What each role can do">
          {roles.loading ? (
            <Loading />
          ) : (
            <div className="grid grid--two">
              {(roles.data ?? []).map((role) => (
                <div key={role.code}>
                  <strong>{role.label}</strong>
                  <p className="small muted" style={{ margin: "2px 0 6px" }}>
                    {role.description}
                  </p>
                  <div className="row" style={{ gap: 4 }}>
                    {role.permissions.map((permission) => (
                      <span key={permission} className="badge badge--neutral tiny">
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {inviting ? (
        <InviteModal
          organizationId={organizationId}
          assignable={assignable.data ?? ["contributor"]}
          onClose={() => setInviting(false)}
          onSent={(invitation) => {
            setJustSent(invitation);
            setInviting(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {historyFor ? (
        <EmailHistoryModal
          organizationId={organizationId}
          invitation={historyFor}
          onClose={() => setHistoryFor(undefined)}
        />
      ) : null}
    </>
  );
}

function InviteModal({
  organizationId,
  assignable,
  onClose,
  onSent,
}: {
  organizationId: string;
  assignable: string[];
  onClose: () => void;
  onSent: (invitation: Invitation) => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roleCode, setRoleCode] = useState(
    assignable.includes("contributor") ? "contributor" : (assignable[0] ?? "viewer"),
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    setError(undefined);
    try {
      const invitation = await invitationsApi.create(organizationId, {
        email,
        role_code: roleCode,
        full_name: fullName || undefined,
        message: message || undefined,
      });
      onSent(invitation);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Invite someone"
      onClose={onClose}
      footer={
        <>
          <button className="btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={!email || busy} onClick={() => void send()}>
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <Field label="Email">
        <input
          type="email"
          value={email}
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="Name" hint="Optional, but the email reads better with it.">
        <input value={fullName} onChange={(event) => setFullName(event.target.value)} />
      </Field>
      <Field label="Role">
        <select value={roleCode} onChange={(event) => setRoleCode(event.target.value)}>
          {assignable.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Personal note"
        hint="Included in the email. People answer more when they know why they were asked."
      >
        <textarea
          rows={3}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="You know the pick and pack process better than anyone — could you walk us through it?"
        />
      </Field>
    </Modal>
  );
}

function EmailHistoryModal({
  organizationId,
  invitation,
  onClose,
}: {
  organizationId: string;
  invitation: Invitation;
  onClose: () => void;
}) {
  const history = useAsync(
    () => invitationsApi.emails(organizationId, invitation.id),
    [organizationId, invitation.id],
  );

  return (
    <Modal title={`Emails to ${invitation.email}`} onClose={onClose}>
      {history.loading ? (
        <Loading />
      ) : !history.data?.length ? (
        <EmptyState title="Nothing sent yet" />
      ) : (
        <div className="stack">
          {history.data.map((message) => (
            <div key={message.id} className="evidence">
              <div className="evidence__ref">
                <StatusBadge status={message.status} /> {message.template} · {message.backend}
              </div>
              <div className="small">{message.subject}</div>
              <div className="tiny faint">
                {message.sent_at
                  ? `Sent ${new Date(message.sent_at).toLocaleString()}`
                  : (message.error ?? "Not sent")}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
